import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import ListPlaceholder from 'src/components/Dashboard/ListPlaceholder';
import displayFilterBarButtons from 'src/components/Dashboard/helpers';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import { FilterBar } from 'src/components/Filter';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import {
  ACTIVE_STATE, ACTIVE_STATE_ACTIVE, DEFAULT_LIST_PAGE_SIZE, LIST_FILTER_ALL,
} from 'src/util/constants';
import logger from 'src/util/logger';

import { PortfolioListCards } from './PortfolioListCards';
import { hasPortfolio, portfoliosWithActiveState } from './helpers';

const messages = defineMessages({
  paginationError: {
    id: 'portfolio.portfolio_list.pagination_error.message',
    defaultMessage: 'Failed to load more portfolios. Please try again.',
  },
});

const PortfolioListFragment = graphql`
  fragment PortfolioList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "PortfolioListRefetchQuery")
  @argumentDefinitions (
    cursor: { type: "String" }
    count: { type: "Int"}
  )
  {
    id
    portfolios(first: $count, after: $cursor)
      @connection(key: "PortfoliosList_portfolios") {
      edges {
        cursor
        node {
          uuid
          title
          active {
            start
            finish
          }
          propertyMembers(first: 500) {
            edges {
              node {
                uuid
              }
            }
          }
          userMembers(first: 500) {
            edges {
              node {
                uuid
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

/**
 * @param {object} param
 * @param {object} param.viewer - viewer object
 * @returns {React.ReactComponentElement} - PortfolioList component
 */
function PortfolioList({ viewer }) {
  const {
    data, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(
    PortfolioListFragment,
    viewer,
  );

  const intl = useIntl();
  const [paginationError, setPaginationError] = useState(null);
  const [activeState, setActiveState] = useState(ACTIVE_STATE_ACTIVE);

  const handlePaginationErrorDismiss = () => setPaginationError(null);

  const copyPaginationErrorModalBodyText = intl.formatMessage(messages.paginationError);

  const { portfolios } = data || {};

  const pageTitle = intl.formatMessage({ id: 'portfolio.portfolio_list.page.title', defaultMessage: 'Enosi - your portfolios' });

  const filterBarButtons = displayFilterBarButtons(intl, activeState);

  /**
   * Updates the filter state based on the provided group and value.
   * @param {object} param0 - An object containing the group and value to update the filter with.
   * @param {string} param0.group - The group to update the filter for.
   * @param {string} param0.value - The value to update the filter to.
   * @returns {void}
   */
  const updateFilter = ({ group, value }) => {
    if (value === LIST_FILTER_ALL) {
      setActiveState(LIST_FILTER_ALL);
      return;
    }
    if (group === ACTIVE_STATE) {
      setActiveState(value);
    }
  };

  /**
   * Loads the next page of data for the portfolio list.
   * @returns {void}
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };
  const copyLoadMore = intl.formatMessage({ id: 'portfolio.portfolio_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'portfolio.portfolio_list.load_more_button.state_no_more.label', defaultMessage: 'No more portfolios to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      <Breadcrumbs breadcrumbs={[{ name: <FormattedMessage id="portfolio.portfolio_list.breadcrumbs.portfolios.title" defaultMessage="Portfolios" /> }]} />
      <FilterBar buttons={filterBarButtons} onButtonClickFunc={updateFilter} />
      <div className={!hasPortfolio(portfolios) || portfoliosWithActiveState(portfolios, activeState).length === 0 ? 'mt-2 mb-4' : 'row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 mt-2'}>
        <PortfolioListCards portfolios={portfolios} activeState={activeState} />
      </div>
      {paginationError && (
        <PlaceholderErrorModal
          bodyText={copyPaginationErrorModalBodyText}
          error={paginationError}
          onCancelFunc={handlePaginationErrorDismiss}
          onRetryFunc={loadMore}
          placeholderComponent={() => <ListPlaceholder minimal />}
          retryDisabled={isLoadingNext}
        />
      )}
      <div>
        <LoadMoreButton
          copyLoadMore={copyLoadMore}
          copyNoMore={copyNoMore}
          hasNext={hasNext}
          onClick={loadMore}
        />
      </div>
    </>
  );
}

export default PortfolioList;

PortfolioList.propTypes = {
  relay: PropTypes.shape({
    hasNext: PropTypes.func,
    isLoadingNext: PropTypes.func,
    loadNext: PropTypes.func,
  }),
  viewer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};
